import { SOFTLINE_FEATURE_WWS_WARENUEBERNAHME } from '../warenuebernahme.shared';
import {
  createAction,
  createGetter,
  CustomStoreAddOn, isDefinedNotEmpty,
  on,
  RemoteCollectionStore,
  select,
  StoreBuilder,
} from '@softline/core';
import { lastValueFrom } from 'rxjs';
import { BewegungenService } from '../services/bewegungen-service';
import {
  Bewe,
  PositionErledigenMischPaletteParams,
  PositionErledigenParams,
} from '../types/bewegung';
import { Emballage } from '../types/warenuebernahme';
import { WwsWarenuebernahmeStore } from './warenuebernahme.store';

type State = RemoteCollectionStore.State<Bewe>;

const remoteCollectionStore = RemoteCollectionStore.create<Bewe>();

const mutations = {};

const getters = {
  bewegungenMap: createGetter<
    State,
    {
      offeneBewegungen: Bewe[];
      erledigteBewegungen: Bewe[];
      alleBewegungen: Bewe[];
      hauptLagerplatzArtikel: Bewe[];
    }
  >('wwsWarenuebernahmeBewegung/getters/bewegungenMap'),
};

const actions = {
  positionErledigen: createAction<
    State,
    PositionErledigenParams,
    null | 'kopf' | 'bewegung'
  >('wwsWarenuebernahmeBewegung/actions/positionErledigen'),
  positionErledigenMischPalette: createAction<
    State,
    PositionErledigenMischPaletteParams,
    null | 'kopf' | 'bewegung'
  >('wwsWarenuebernahmeBewegung/actions/positionErledigenMischPalette'),
  positionOeffnen: createAction<State, Bewe, void>(
    'wwsWarenuebernahmeBewegung/actions/positionOeffnen'
  ),
  emballageHinzufuegen: createAction<State, { emballage: Emballage; idwu: string }, void>(
    'wwsWarenuebernahmeBewegung/actions/emballageHinzufuegen'
  ),
};

const WwsWarenuebernahmeBewegungStoreExtension: CustomStoreAddOn<
  State,
  typeof mutations,
  typeof getters,
  typeof actions
> = {
  mutations,
  getters,
  actions,
  feature: {
    getters: [
      select(getters.bewegungenMap, ({ state }) => {
        const entities = state.ids.map((id) => state.entities[id] as Bewe);
        const offeneBewegungen: Bewe[] = [];
        const erledigteBewegungen: Bewe[] = [];
        const hauptLagerplatzArtikel: Bewe[] = [];

        entities.forEach((entity) => {
          if (
            (entity?.artikelMenge?.artikel?.kzartikelart === 'DIF' ||
              entity?.artikelMenge?.artikel?.kzartikelart === 'SDIF') &&
            entity?.bewelgs?.every((o) => o.status === 'E')
          )
            erledigteBewegungen.push(entity);
          else if (entity.status === 'O') {
            offeneBewegungen.push(entity)
            if(isDefinedNotEmpty(entity.lagerplatz)) hauptLagerplatzArtikel.push(entity)
          }
          else erledigteBewegungen.push(entity);
        });

        const bewes = {
          offeneBewegungen: offeneBewegungen,
          erledigteBewegungen: erledigteBewegungen,
          alleBewegungen: entities,
          hauptLagerplatzArtikel: hauptLagerplatzArtikel
        };

        return bewes;
      }),
    ],
    actions: [
      on(actions.positionErledigen, async ({ params, featureName, injector, commit }) => {
        const service = injector.get(BewegungenService);

        if (params.payload.bewegung.artikelMenge.menge === 0) {
          const result = await lastValueFrom(
            service.positionKonroll(params.payload.bewegung.id)
          );

          console.log(result);

          if (result.geloescht === 'bewegung') {
            commit(
              featureName,
              remoteCollectionStore.mutations.remove,
              params.payload.bewegung
            );

            return 'bewegung';
          } else if (result.geloescht === 'kopf') {
            commit(
              SOFTLINE_FEATURE_WWS_WARENUEBERNAHME,
              WwsWarenuebernahmeStore.mutations.remove,
              params.warenuebernahme
            );

            return 'kopf';
          }

          return null;
        } else {
          const result = await lastValueFrom(service.positionErledigen(params.payload));

          console.log('ID RESULT: ', result);

          commit(featureName, remoteCollectionStore.mutations.addOrUpdate, result);

          return null;
        }
      }),
      on(
        actions.positionErledigenMischPalette,
        async ({ params, featureName, injector, commit }) => {
          const service = injector.get(BewegungenService);

          if (params.payload.bewegung.artikelMenge.menge === 0) {
            const result = await lastValueFrom(
              service.positionKonroll(params.payload.bewegung.id)
            );

            console.log(result);

            if (result.geloescht === 'bewegung') {
              commit(
                featureName,
                remoteCollectionStore.mutations.remove,
                params.payload.bewegung
              );

              return 'bewegung';
            } else if (result.geloescht === 'kopf') {
              commit(
                SOFTLINE_FEATURE_WWS_WARENUEBERNAHME,
                WwsWarenuebernahmeStore.mutations.remove,
                params.warenuebernahme
              );

              return 'kopf';
            }

            return null;
          } else {
            const result = await lastValueFrom(
              service.positionErledigenMischPalette(params.payload)
            );

            console.log('ID RESULT: ', result.bewegung);

            commit(
              featureName,
              remoteCollectionStore.mutations.addOrUpdate,
              result.bewegung
            );

            return null;
          }
        }
      ),
      on(actions.positionOeffnen, async ({ params, featureName, injector, commit }) => {
        const service = injector.get(BewegungenService);

        const result = await lastValueFrom(service.positionOeffnen(params));

        commit(featureName, remoteCollectionStore.mutations.addOrUpdate, result);
      }),
      on(
        actions.emballageHinzufuegen,
        async ({ params, featureName, injector, commit }) => {
          const service = injector.get(BewegungenService);

          const result = await lastValueFrom(
            service.emballageHinzufuegen(params.emballage, params.idwu)
          );

          commit(featureName, remoteCollectionStore.mutations.addOrUpdate, result);
        }
      ),
    ],
    mutations: [],
  },
};

export const WwsBewegungStore = new StoreBuilder(remoteCollectionStore).add(
  WwsWarenuebernahmeBewegungStoreExtension
).value;
